import type { EngineScore } from "@onchainreps/chess";
import type { EngineLineView, PositionAnalysis } from "../lib/analysis";

interface EnginePanelProps {
  position: PositionAnalysis | undefined;
}

export function EnginePanel({ position }: EnginePanelProps) {
  if (!position) return null;

  const lines = position.lines.length
    ? position.lines
    : position.pv.length
      ? [{ san: position.pv, score: position.score, depth: position.depth }]
      : [];

  if (!lines.length) {
    return (
      <div className="enginebar enginebar--idle">
        <span className="enginebar__eval">—</span>
        <span className="enginebar__line">The engine is still thinking.</span>
      </div>
    );
  }

  const [best] = lines;
  const alternatives = lines.slice(1);

  return (
    <div className="enginebar">
      <div className="enginebar__top">
        <span className="enginebar__eval">{formatEval(best?.score ?? null, position.sideToMove)}</span>
        <span className="enginebar__line">
          {best?.san.length ? best.san.join(" ") : "No line yet"}
        </span>
        <span className="enginebar__meta">
          depth {position.depth}
          {alternatives.length ? ` · ${alternatives.length + 1} lines` : ""}
        </span>
      </div>
      {alternatives.length ? (
        <ul className="enginebar__alts">
          {alternatives.map((line, index) => (
            <li className="enginebar__alt" key={`${index}-${line.san.join(" ")}`}>
              <span className="enginebar__alt-eval">{formatEval(line.score, position.sideToMove)}</span>
              <span className="enginebar__alt-line">{line.san.join(" ")}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function formatEval(score: EngineScore | null, sideToMove: "white" | "black"): string {
  if (!score) return "—";
  const sign = sideToMove === "white" ? 1 : -1;
  if (score.type === "mate") {
    const moves = score.value * sign;
    return moves > 0 ? `M${moves}` : `-M${Math.abs(moves)}`;
  }
  const centipawns = (score.value * sign) / 100;
  const rounded = Math.abs(centipawns) >= 10 ? centipawns.toFixed(0) : centipawns.toFixed(2);
  return centipawns >= 0 ? `+${rounded}` : rounded;
}
